import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';
import { useUser } from '../../context/UserContext';
import {
  Calculator, Shapes, Ruler, BarChart3, Code2, Percent, Mic,
  BookOpen, PenTool, Library, CheckCircle, Palette, BookOpenIcon,
  Eye, UserPlus, Zap, Users, TrendingUp, ArrowRight, Sparkles, FileQuestion
} from 'lucide-react';

const niveaux = [
  { key: 'aBesoins', label: 'À besoins', color: 'bg-rose-500', hex: '#f43f5e', ring: 'ring-rose-400/40', textColor: 'text-rose-600' },
  { key: 'fragile', label: 'Fragile', color: 'bg-amber-500', hex: '#f59e0b', ring: 'ring-amber-400/40', textColor: 'text-amber-600' },
  { key: 'satisfaisant', label: 'Satisfaisant', color: 'bg-emerald-500', hex: '#10b981', ring: 'ring-emerald-400/40', textColor: 'text-emerald-600' },
  { key: 'tresSatisfaisant', label: 'Très satisf.', color: 'bg-teal-600', hex: '#0d9488', ring: 'ring-teal-400/40', textColor: 'text-teal-700' },
];

export const getThemeIcon = (themeName) => {
  const normalizedName = themeName?.replace(/\u2019/g, "'");
  const map = {
    'Nombre et Calculs': Calculator,
    'Nombre, calcul et résolution de problèmes': Calculator,
    'Espace et Géométrie': Shapes,
    'Grandeurs et mesures': Ruler,
    'Organisation et gestion de données, fonctions': BarChart3,
    'Organisation et gestion de données et probabilités': BarChart3,
    'Algorithmique et programmation': Code2,
    'Initiation à la pensée informatique': Code2,
    'La proportionnalité': Percent,
    'Langage Oral': Mic,
    'Lecture': BookOpen,
    "Lecture et compréhension de l'écrit": BookOpen,
    'Écriture': PenTool,
    'Vocabulaire': Library,
    'Grammaire et orthographe grammaticale': CheckCircle,
    'Étude de la langue': CheckCircle,
    'Culture littéraire et artistique': Palette,
  };
  return map[normalizedName] || BookOpenIcon;
};

const ElevesPopover = ({ eleves, niveau, anchorRef, visible }) => {
  const [pos, setPos] = useState(null);

  useEffect(() => {
    if (!visible || !anchorRef?.current) { setPos(null); return; }
    const rect = anchorRef.current.getBoundingClientRect();
    setPos({ top: rect.top, left: rect.left });
  }, [visible, anchorRef]);

  if (!visible || !eleves || eleves.length === 0 || !pos) return null;

  return createPortal(
    <div
      style={{ position: 'fixed', top: pos.top, left: pos.left, transform: 'translateY(-100%) translateY(-8px)' }}
      className="z-[99999] pointer-events-auto"
    >
      <div className="bg-white backdrop-blur-xl border border-blue-100 rounded-2xl xl:rounded-xl 2xl:rounded-xl 3xl:rounded-2xl p-2.5 xl:p-1.5 2xl:p-2 3xl:p-2.5 shadow-2xl
       shadow-blue-500/10 min-w-[180px] xl:min-w-[120px] 2xl:min-w-[150px] 3xl:min-w-[180px]">
        <div className="absolute -bottom-2 left-2 w-4 h-4 xl:w-3 xl:h-3 3xl:w-4 3xl:h-4 bg-white border-r border-b border-blue-100 rotate-45" />

        <div className="flex items-center gap-2 xl:gap-1 2xl:gap-1.5 3xl:gap-2 mb-2 xl:mb-1 2xl:mb-1.5 3xl:mb-2 pb-2 xl:pb-1 2xl:pb-1.5 3xl:pb-2 border-b border-blue-50">
          <div className={`w-2.5 h-2.5 xl:w-2 xl:h-2 3xl:w-2.5 3xl:h-2.5 rounded-full ${niveau.color} ring-4 xl:ring-2 3xl:ring-4 ring-opacity-20 ${niveau.ring}`} />
          <span className="text-slate-800 text-sm xl:text-[10px] 2xl:text-xs 3xl:text-sm font-bold">{niveau.label}</span>
          <span className="text-blue-400 text-xs xl:text-[9px] 2xl:text-[10px] 3xl:text-xs font-medium ml-auto bg-blue-50 px-2 xl:px-1 2xl:px-1.5 3xl:px-2 py-0.5 rounded-full">{eleves.length}</span>
        </div>

        <div className="space-y-1 xl:space-y-0 2xl:space-y-0.5 3xl:space-y-1">
          {eleves.map((eleve, idx) => (
            <div
              key={eleve.id || idx}
              className="flex items-center gap-2 xl:gap-1 2xl:gap-1.5 3xl:gap-2 py-1.5 xl:py-0.5 2xl:py-1 3xl:py-1.5 px-2 xl:px-1 2xl:px-1.5 3xl:px-2 rounded-xl hover:bg-gradient-to-r hover:from-blue-50 hover:to-cyan-50 cursor-pointer transition-all duration-200"
            >
              <div className="w-6 h-6 xl:w-4 xl:h-4 2xl:w-5 2xl:h-5 3xl:w-6 3xl:h-6 rounded-full bg-gradient-to-br from-blue-500 via-cyan-500 to-teal-500 flex items-center justify-center text-[10px] xl:text-[8px] 2xl:text-[9px] 3xl:text-[10px] text-white font-bold shadow-md shadow-blue-500/30">
                {eleve.prenom?.[0]}{eleve.nom?.[0]}
              </div>
              <span className="text-slate-700 text-sm xl:text-[10px] 2xl:text-xs 3xl:text-sm font-medium">{eleve.prenom} {eleve.nom?.[0]}.</span>
            </div>
          ))}
        </div>
      </div>
    </div>,
    document.body
  );
};

/* ── Camembert SVG ── */
const PieChart = ({ data, size = 140 }) => {
  const total = data.reduce((sum, d) => sum + d.value, 0);
  if (total === 0) return null;

  const cx = size / 2;
  const cy = size / 2;
  const radius = size / 2 - 4;
  let cumulativeAngle = -90; // start from top

  const slices = data.filter(d => d.value > 0).map(d => {
    const angle = (d.value / total) * 360;
    const startAngle = cumulativeAngle;
    cumulativeAngle += angle;
    const endAngle = cumulativeAngle;

    const startRad = (Math.PI / 180) * startAngle;
    const endRad = (Math.PI / 180) * endAngle;

    const x1 = cx + radius * Math.cos(startRad);
    const y1 = cy + radius * Math.sin(startRad);
    const x2 = cx + radius * Math.cos(endRad);
    const y2 = cy + radius * Math.sin(endRad);

    const largeArc = angle > 180 ? 1 : 0;

    const path = `M ${cx} ${cy} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} Z`;

    return { ...d, path };
  });

  return (
    <svg width={size} height={size} className="drop-shadow-md">
      {slices.map((slice, i) => (
        <path
          key={i}
          d={slice.path}
          fill={slice.hex}
          stroke="white"
          strokeWidth="2"
          className="transition-all duration-500 hover:opacity-80"
        />
      ))}
      {/* Inner circle for donut effect */}
      <circle cx={cx} cy={cy} r={radius * 0.3} fill="white" className="drop-shadow-sm" />
    </svg>
  );
};

/* ── Légende row avec Eye popover ── */
const LegendRow = ({ niveau, data }) => {
  const hasEleves = data && data.eleves && data.eleves.length > 0;
  const btnRef = useRef(null);
  const [hovered, setHovered] = useState(false);

  return (
    <div className="flex items-center gap-2.5 py-1.5">
      <div className={`w-3 h-3 rounded-full ${niveau.color} shadow-sm shrink-0`} />
      <span className="text-slate-600 text-xs xl:text-[10px] 2xl:text-[11px] 3xl:text-[11px] font-semibold">{niveau.label}</span>
      <span className={`font-bold text-sm xl:text-xs 2xl:text-xs 3xl:text-xs ${niveau.textColor} w-8 text-right`}>
        {data?.percent || 0}%
      </span>
      <div
        className="relative"
        onMouseEnter={() => hasEleves && setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <button
          ref={btnRef}
          disabled={!hasEleves}
          className={`
            flex items-center gap-1 px-2 py-1 rounded-lg text-xs xl:text-[10px] 2xl:text-[11px] 3xl:text-xs font-semibold
            transition-all duration-300
            ${hasEleves
              ? 'bg-white hover:bg-blue-50 text-slate-600 hover:text-blue-600 shadow-sm hover:shadow-md ring-1 ring-slate-200 hover:ring-blue-300 cursor-pointer'
              : 'bg-slate-50 text-slate-300 cursor-not-allowed'
            }
          `}
        >
          <Eye className="w-3 h-3" />
          <span>{data?.eleves?.length || 0}</span>
        </button>
        {hasEleves && <ElevesPopover eleves={data.eleves} niveau={niveau} anchorRef={btnRef} visible={hovered} />}
      </div>
    </div>
  );
};

// eslint-disable-next-line no-unused-vars
const ThemeClassePieChart = ({ chartsData, progressData, theme, evalCountByTheme, nature, compact = false }) => {
  const navigate = useNavigate();
  const { idclasse } = useParams();
  const { user } = useUser();
  const isChef = user?.role === 'chef';
  const themeProgress = progressData?.[theme.nom];
  const hasData = themeProgress?.hasData;

  const difficultyRate = hasData
    ? (themeProgress.aBesoins.percent + themeProgress.fragile.percent)
    : 0;

  const nbElevesEnDifficulte = hasData
    ? (themeProgress.aBesoins.eleves.length + themeProgress.fragile.eleves.length)
    : 0;

  const handleCreateGroupFromDifficulty = () => {
    navigate(`/home/groupes/${idclasse}`);
  };

  const evalCount = evalCountByTheme?.[theme.nom] || 0;

  // Pie data
  const pieData = hasData ? niveaux.map(n => ({
    key: n.key,
    label: n.label,
    hex: n.hex,
    value: themeProgress[n.key]?.eleves?.length || 0,
  })) : [];

  if (!hasData) {
    return (
      <div className="lg:h-full flex flex-col items-center justify-center py-8 xl:py-5 2xl:py-6 3xl:py-8 text-center">
        <div className="w-14 h-14 xl:w-10 xl:h-10 2xl:w-12 2xl:h-12 3xl:w-14 3xl:h-14 rounded-2xl bg-gradient-to-br from-blue-100 to-cyan-100 border border-blue-200/60 flex items-center justify-center mb-3 shadow-lg shadow-blue-500/10">
          <Sparkles className="w-7 h-7 xl:w-5 xl:h-5 2xl:w-6 2xl:h-6 3xl:w-7 3xl:h-7 text-blue-500" />
        </div>
        <p className="text-slate-700 text-base xl:text-sm 2xl:text-sm 3xl:text-base font-bold mb-1">Aucune donnée</p>
        <p className="text-slate-400 text-sm xl:text-xs 2xl:text-xs 3xl:text-sm mb-4">Évaluez ce thème pour suivre vos élèves</p>
        {nature === "theme" && !isChef && (
          <button onClick={() => navigate(`/home/evals/${idclasse}/add`)} className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 via-green-500 to-teal-500 hover:from-emerald-600 hover:via-green-600 hover:to-teal-600 text-white text-sm font-bold transition-all duration-300 shadow-lg shadow-emerald-500/30 hover:shadow-xl hover:shadow-emerald-500/40 hover:scale-[1.02]">
            <FileQuestion className="w-4 h-4" />
            Créer une évaluation
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="lg:h-full flex flex-col">
      {/* Contenu scrollable */}
      <div className="flex-1 min-h-0 flex flex-col items-center justify-center gap-3 xl:flex-row xl:items-center xl:justify-center xl:gap-4 2xl:flex-col 2xl:items-center 2xl:justify-center 2xl:gap-3">
        {/* Nombre d'évaluations */}
        {nature === "theme" && evalCount > 0 && (
          <span className="text-slate-400 text-xs font-medium xl:hidden 2xl:block">{evalCount} évaluation{evalCount > 1 ? 's' : ''} sur ce thème</span>
        )}

        {/* Camembert */}
        <div className="flex items-center justify-center shrink-0">
          <PieChart data={pieData} size={compact ? 100 : 180} />
        </div>

        {/* Légende */}
        <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 xl:flex-col xl:gap-y-0.5 xl:gap-x-0 2xl:flex-row 2xl:gap-x-4 2xl:gap-y-1">
          {niveaux.map(niveau => (
            <LegendRow
              key={niveau.key}
              niveau={niveau}
              data={themeProgress[niveau.key]}
            />
          ))}
        </div>
      </div>

      {/* CTA Footer - fixe en bas */}
      {!isChef && <div className="shrink-0 pt-2 flex justify-center">
        <button
          onClick={handleCreateGroupFromDifficulty}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-xl
            bg-gradient-to-r ${
              difficultyRate > 30
                ? 'from-orange-500 via-orange-600 to-amber-600 hover:from-orange-600 hover:via-orange-700 hover:to-amber-700 shadow-orange-500/30 hover:shadow-orange-500/40'
                : 'from-emerald-500 via-green-500 to-teal-500 hover:from-emerald-600 hover:via-green-600 hover:to-teal-600 shadow-emerald-500/30 hover:shadow-emerald-500/40'
            }
            text-white text-sm font-bold transition-all duration-300
            shadow-lg hover:shadow-xl hover:scale-[1.02]`}
        >
          {difficultyRate > 30
            ? <Zap className="w-4 h-4" />
            : <Sparkles className="w-4 h-4" />
          }
          {difficultyRate > 30
            ? `${nbElevesEnDifficulte} élève${nbElevesEnDifficulte > 1 ? 's' : ''} en difficulté`
            : `${100 - difficultyRate}% de réussite !`
          }
        </button>
      </div>}
    </div>
  );
};

export default ThemeClassePieChart;
